/**
 * AccessExplanation — the "why do I have this?" derivation chain. This is the
 * signature moment of the whole feature: it turns an OpenFGA relation path into
 * a calm, scannable ladder a nurse reads in one glance.
 *
 * Rendered vertically (not a horizontal breadcrumb) on purpose: German relation
 * labels run long, and a ladder handles long labels, wraps gracefully, and
 * scans one-handed on iPhone. The implicit head is "You"; each hop is a node
 * with the edge (relation) captioned on the connector leading into it; the last
 * node IS the resource and carries the effective-role badge. A time-bound hop
 * shows its own expiry chip, so "this membership lapses tonight" is visible at
 * exactly the hop that expires.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { SOURCE_META } from '@frontend/components/access/accessMeta'
import { ExpiryChip, RoleBadge, relationLabel } from '@frontend/components/access/atoms'
import type { ExplanationStep, UserRef } from '@frontend/components/access/types'
import { UserRound, type LucideIcon } from 'lucide-react'

interface ChainNode {
  key: string
  icon: LucideIcon
  /** Node marker fill + ring classes. */
  marker: string
  /** Edge caption on the connector leading into this node (null for the head). */
  edge: string | null
  label: string
  expiresAt?: string
  isResource: boolean
}

export interface AccessExplanationProps {
  subject: UserRef
  steps: ExplanationStep[]
  /** Effective role at the endpoint — rendered as the resource node's badge. */
  resourceRole?: string
  now?: number
  className?: string
}

export function AccessExplanation({
  subject,
  steps,
  resourceRole,
  now,
  className,
}: AccessExplanationProps) {
  const { t } = useTranslation()

  const nodes: ChainNode[] = [
    {
      key: 'head',
      icon: UserRound,
      // The origin = you: the one place the Action-Blue accent marks a node,
      // because "you" is not a category, it is the subject.
      marker: 'bg-primary text-primary-foreground ring-primary/25',
      edge: null,
      label: subject.name,
      isResource: false,
    },
    ...steps.map((s, i) => {
      const meta = SOURCE_META[s.kind]
      return {
        key: `${s.via.type}:${s.via.id}:${i}`,
        icon: meta.icon,
        marker: meta.node,
        edge: relationLabel(t, s.relation),
        label: s.via.label,
        expiresAt: s.expires_at,
        isResource: i === steps.length - 1,
      }
    }),
  ]

  return (
    <ol
      className={cn(
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200',
        className,
      )}
      aria-label={t('access.explanation.title')}
    >
      {nodes.map((node, i) => {
        const isLast = i === nodes.length - 1
        return (
          <li key={node.key} className="relative flex gap-3 pb-4 last:pb-0">
            {/* Connector to the next node — the continuous rail. */}
            {!isLast && (
              <span
                className="absolute left-[13px] top-7 bottom-0 w-px bg-neutral-200"
                aria-hidden="true"
              />
            )}
            <span
              className={cn(
                'relative z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full ring-1',
                node.marker,
                node.isResource && 'ring-2',
              )}
              aria-hidden="true"
            >
              <node.icon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 pt-0.5">
              {node.edge && (
                <p className="text-[12px] leading-tight text-neutral-500">{node.edge}</p>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <p
                  className={cn(
                    'text-[14px] font-semibold text-neutral-900',
                    node.isResource && 'text-primary',
                  )}
                >
                  {node.label}
                </p>
                {node.isResource && resourceRole && <RoleBadge role={resourceRole} />}
              </div>
              {node.expiresAt && (
                <div className="mt-1">
                  <ExpiryChip expiresAt={node.expiresAt} now={now} />
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
